import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import {
  PhotoCamera,
  Upload,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { supabase } from '../../utils/supabase';

export default function ReceiptScanner({ open, onClose, onIngredientsExtracted }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [extractedItems, setExtractedItems] = useState([]);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Debug logging for component state changes
  React.useEffect(() => {
    console.log('ReceiptScanner state changed:', {
      open,
      selectedFile: selectedFile ? { name: selectedFile.name, size: selectedFile.size } : null,
      hasPreview: !!preview,
      loading,
      error,
      extractedItemsCount: extractedItems.length,
      isMobile
    });
  }, [open, selectedFile, preview, loading, error, extractedItems, isMobile]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    console.log('File selected via upload:', file ? {
      name: file.name,
      size: file.size,
      type: file.type
    } : 'No file');
    
    if (file) {
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        setPreview(URL.createObjectURL(file));
        setError(null);
        console.log('File successfully set for analysis');
      } else {
        console.error('Invalid file type selected:', file.type);
        setError('Please select an image file');
      }
    }
  };

  const handleCameraCapture = (event) => {
    const file = event.target.files[0];
    console.log('File captured via camera:', file ? {
      name: file.name,
      size: file.size,
      type: file.type
    } : 'No file');
    
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
      setError(null);
      console.log('Camera capture successfully set for analysis');
    }
  };

  const handleAnalyzeReceipt = async () => {
    if (!selectedFile) {
      console.log('No file selected for analysis');
      return;
    }

    const analysisStartTime = Date.now();
    console.log(`[${new Date().toISOString()}] Starting receipt analysis`);
    console.log('Selected file details:', {
      name: selectedFile.name,
      size: selectedFile.size,
      type: selectedFile.type,
      lastModified: selectedFile.lastModified
    });

    try {
      setLoading(true);
      setError(null);

      console.log('Converting image to base64...');
      const conversionStartTime = Date.now();
      
      // Convert image to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const conversionTime = Date.now() - conversionStartTime;
          console.log(`Image conversion completed in ${conversionTime}ms`);
          
          const base64Image = e.target.result.split(',')[1];
          console.log(`Base64 image length: ${base64Image.length} characters`);
          
          const requestPayload = {
            image: base64Image,
            mimeType: selectedFile.type
          };
          
          console.log('Request payload prepared:', {
            imageLength: base64Image.length,
            mimeType: selectedFile.type
          });
          
          // Call Supabase Edge Function to analyze receipt
          console.log('Calling Supabase Edge Function...');
          const apiCallStartTime = Date.now();
          
          const { data, error } = await supabase.functions.invoke('analyze-receipt', {
            body: requestPayload
          });

          const apiCallTime = Date.now() - apiCallStartTime;
          console.log(`Supabase function call completed in ${apiCallTime}ms`);
          
          if (error) {
            console.error('Supabase function error:', error);
            throw error;
          }

          console.log('Supabase function response:', data);

          if (data && data.items && data.items.length > 0) {
            console.log(`Successfully extracted ${data.items.length} items:`, data.items);
            setExtractedItems(data.items);
          } else {
            console.warn('No items found in response:', data);
            setError('No food items found in the receipt. Please try a clearer image or a different receipt.');
          }
        } catch (err) {
          const errorTime = Date.now() - analysisStartTime;
          console.error(`Error analyzing receipt after ${errorTime}ms:`, err);
          console.error('Error details:', {
            message: err.message,
            stack: err.stack,
            name: err.name
          });
          
          let errorMessage = 'Failed to analyse receipt. Please try again with a clearer image.';
          
          if (err.message && err.message.includes('timeout')) {
            errorMessage = 'Analysis timed out. Please try a smaller or clearer image.';
          } else if (err.message && err.message.includes('Rate limit')) {
            errorMessage = 'Service temporarily busy. Please wait a moment and try again.';
          }
          
          setError(errorMessage);
        } finally {
          const totalTime = Date.now() - analysisStartTime;
          console.log(`Total analysis completed in ${totalTime}ms`);
          setLoading(false);
        }
      };
      
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        setError('Failed to read image file. Please try again.');
        setLoading(false);
      };
      
      reader.readAsDataURL(selectedFile);
    } catch (err) {
      const errorTime = Date.now() - analysisStartTime;
      console.error(`Outer error processing image after ${errorTime}ms:`, err);
      setError('Failed to process image. Please try again.');
      setLoading(false);
    }
  };

  const handleAddToPantry = () => {
    console.log('Adding extracted items to pantry:', extractedItems);
    onIngredientsExtracted(extractedItems);
    handleClose();
  };

  const handleClose = () => {
    console.log('Closing ReceiptScanner, cleaning up state');
    setSelectedFile(null);
    setPreview(null);
    setExtractedItems([]);
    setError(null);
    onClose();
  };

  const handleRetake = () => {
    setSelectedFile(null);
    setPreview(null);
    setExtractedItems([]);
    setError(null);
  };

  // Clean up object URL when component unmounts or preview changes
  React.useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const getConfidenceColor = (confidence) => {
    switch (confidence?.toLowerCase()) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'error';
      default: return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Scan Receipt</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!selectedFile ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              {isMobile ? 'Take a photo or upload your receipt' : 'Upload your receipt'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              I'll analyse the receipt and add food items to your pantry using British spelling
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {isMobile && (
                <Button
                  variant="contained"
                  startIcon={<PhotoCamera />}
                  onClick={() => cameraInputRef.current?.click()}
                  size="large"
                >
                  Take Photo
                </Button>
              )}
              
              <Button
                variant="outlined"
                startIcon={<Upload />}
                onClick={() => fileInputRef.current?.click()}
                size="large"
              >
                Upload Image
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Supported formats: JPG, PNG, WEBP
            </Typography>

            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            {isMobile && (
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCameraCapture}
                style={{ display: 'none' }}
              />
            )}
          </Box>
        ) : (
          <Box>
            {/* Image Preview */}
            <Paper sx={{ p: 2, mb: 2, textAlign: 'center' }}>
              <img
                src={preview}
                alt="Receipt preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '300px',
                  objectFit: 'contain',
                  borderRadius: '4px'
                }}
              />
              <Box sx={{ mt: 2 }}>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={handleRetake}
                  size="small"
                  variant="outlined"
                >
                  Choose Different Image
                </Button>
              </Box>
            </Paper>

            {/* Analysis Results */}
            {extractedItems.length > 0 ? (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckIcon color="success" sx={{ mr: 1 }} />
                  Found {extractedItems.length} food items:
                </Typography>
                <Paper sx={{ maxHeight: 300, overflowY: 'auto', border: 1, borderColor: 'divider' }}>
                  <List dense>
                    {extractedItems.map((item, index) => (
                      <ListItem key={index} divider={index < extractedItems.length - 1}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" fontWeight="medium" component="span">
                                {item.name}
                              </Typography>
                              {item.confidence && (
                                <Chip
                                  label={item.confidence}
                                  size="small"
                                  color={getConfidenceColor(item.confidence)}
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <>
                              {item.quantity && (
                                <Typography variant="body2" color="text.secondary" component="span" display="block">
                                  Quantity: {item.quantity} {item.unit || ''}
                                </Typography>
                              )}
                              {item.category && (
                                <Typography variant="body2" color="text.secondary" component="span" display="block">
                                  Category: {item.category}
                                </Typography>
                              )}
                              {item.expiry_date && (
                                <Typography variant="body2" color="text.secondary" component="span" display="block">
                                  Estimated expiry: {new Date(item.expiry_date).toLocaleDateString()}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Box>
            ) : !loading && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body1" gutterBottom>
                  Ready to analyse your receipt
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleAnalyzeReceipt}
                  disabled={loading}
                  size="large"
                  sx={{ mt: 1 }}
                >
                  Analyse Receipt
                </Button>
              </Box>
            )}

            {loading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Analysing receipt with AI...
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This may take a few moments
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {extractedItems.length > 0 && (
          <Button 
            variant="contained" 
            onClick={handleAddToPantry}
            disabled={loading}
            startIcon={<CheckIcon />}
          >
            Add {extractedItems.length} Items to Pantry
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
